/**
 * treasury.unreconciled_alert — compliance hygiene card surfacing
 * disbursements that have been posted but not yet reconciled to bank
 * statements. From ticket FB-QBSPQBVZ.
 *
 * Sorted by age descending (oldest at top — those need attention first).
 * Click-through goes to the disbursement detail page.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { WidgetProps } from "../registry";
import { CheckCircle2 } from "lucide-react";
import { Skeleton } from "../../components/ui";

interface Disbursement {
  id: string;
  amount_usd: number;
  payee_name: string | null;
  posted_date: string;
  reconciled_at: string | null;
  age_days?: number;
}

export function TreasuryUnreconciledAlert({ spec, workspaceId }: WidgetProps) {
  const minAgeDays = (spec.params?.min_age_days as number) ?? 7;
  const limit = (spec.params?.limit as number) ?? 20;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "treasury.unreconciled", workspaceId, minAgeDays, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        unreconciled_only: "true",
        min_age_days: String(minAgeDays),
        limit: String(limit),
      });
      const res = await fetch(
        `/api/v1/workspaces/${workspaceId}/disbursements?${params.toString()}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      return (json.disbursements ?? []) as Disbursement[];
    },
  });

  if (error) return <DataUnavailable message="Unreconciled disbursements could not be loaded." retry={refetch} />;
  if (isLoading) {
    return (
      <div role="status" className="space-y-2">
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  if (!data || data.length === 0) {
    return <div className="flex items-center gap-1.5 text-stone-600 text-xs"><CheckCircle2 size={13} className="text-green-700" aria-hidden="true" /> All disbursements reconciled.</div>;
  }

  const total = data.reduce((sum, d) => sum + Number(d.amount_usd ?? 0), 0);
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline justify-between border-b border-stone-100 pb-2">
        <div className="text-stone-700 font-semibold">
          {data.length} unreconciled
        </div>
        <div className="font-mono text-stone-500">${total.toLocaleString()}</div>
      </div>
      <ul className="space-y-1.5">
        {data.map((d) => (
          <li key={d.id} className="flex items-center gap-3">
            <span className="text-stone-700 flex-1 truncate">{d.payee_name ?? d.id.slice(0, 8)}</span>
            <span className="font-mono text-stone-500 text-[11px]">
              {d.age_days ?? "?"}d
            </span>
            <span className="font-mono text-stone-700 tabular-nums w-20 text-right">
              ${Number(d.amount_usd ?? 0).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
